import React from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
} from '@mui/material';
import { Block, Phone, Email, LocationOn } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const BlockedBranchDialog = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Check if user's branch is blocked
  const isBlocked = user?.branch?.status === 'blocked';

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!isBlocked) return null;

  return (
    <Dialog
      open={isBlocked}
      disableEscapeKeyDown
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: (theme) => theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
            : 'linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)',
          border: '2px solid #DC2626',
        }
      }}
    >
      <DialogContent sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', py: 3 }}>
          {/* Warning Icon */}
          <Box
            sx={{
              width: 120,
              height: 120,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #DC2626 0%, #EF4444 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              boxShadow: '0 8px 32px rgba(220, 38, 38, 0.3)',
            }}
          >
            <Block sx={{ fontSize: 64, color: 'white' }} />
          </Box>

          {/* Title */}
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#DC2626', mb: 2 }}>
            🚫 ACCESS DENIED
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Branch Temporarily Blocked
          </Typography>

          {/* Message */}
          <Box sx={{
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
            p: 3,
            borderRadius: 2,
            mb: 3,
            border: '1px solid',
            borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
          }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Your branch <strong>{user?.branch?.name || 'Unknown Branch'}</strong> has been temporarily blocked by the administration.
            </Typography>

            {user?.branch?.blockReason && (
              <Box sx={{
                bgcolor: '#FEF2F2',
                p: 2,
                borderRadius: 1,
                border: '1px solid #DC2626',
                mb: 2
              }}>
                <Typography variant="caption" sx={{ display: 'block', color: '#DC2626', fontWeight: 600, mb: 0.5 }}>
                  REASON FOR BLOCK:
                </Typography>
                <Typography variant="body2" sx={{ color: '#991B1B', fontWeight: 500 }}>
                  {user.branch.blockReason}
                </Typography>
              </Box>
            )}

            <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
              You cannot access the system until this issue is resolved.
            </Typography>
          </Box>

          {/* Contact Information */}
          <Box sx={{
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(79, 70, 229, 0.1)' : 'rgba(79, 70, 229, 0.05)',
            p: 3,
            borderRadius: 2,
            border: '2px solid #4F46E5',
            mb: 3
          }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#4F46E5', mb: 2 }}>
              📞 Contact State Administrative Office
            </Typography>

            <Box sx={{ textAlign: 'left', maxWidth: 400, margin: '0 auto' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <LocationOn sx={{ color: '#4F46E5' }} fontSize="small" />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    BSS IT Mission - State Head Office
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Technology Bhavan, Thiruvananthapuram - 695 010
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Phone sx={{ color: '#4F46E5' }} fontSize="small" />
                <Box>
                  <Typography variant="body2">
                    <strong>Phone:</strong> +91-471-XXXXXXX
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Office Hours: Mon-Fri, 9:00 AM - 6:00 PM IST
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Email sx={{ color: '#4F46E5' }} fontSize="small" />
                <Typography variant="body2">
                  <strong>Email:</strong> admin@bssit.com
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Warning Box */}
          <Box sx={{
            bgcolor: '#FEF3C7',
            p: 2,
            borderRadius: 1,
            border: '2px solid #F59E0B',
            mb: 3
          }}>
            <Typography variant="caption" sx={{ color: '#92400E', fontWeight: 600, display: 'block' }}>
              ⚠️ IMPORTANT: Do not attempt to access the system until the block is removed. Continued attempts may result in permanent suspension.
            </Typography>
          </Box>

          {/* Logout Button */}
          <Button
            variant="contained"
            color="error"
            size="large"
            onClick={handleLogout}
            sx={{
              minWidth: 200,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 700,
              boxShadow: '0 4px 14px rgba(220, 38, 38, 0.4)',
              '&:hover': {
                boxShadow: '0 6px 20px rgba(220, 38, 38, 0.6)',
              }
            }}
          >
            Logout & Exit
          </Button>

          <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary' }}>
            You will be automatically logged out for security purposes.
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BlockedBranchDialog;
